import { request } from './request';

/**
 * The seven windows the API's RANGES allows. A declaration may name its own;
 * otherwise the host's current range applies.
 */
const RANGE_MS = {
  '1h': 3600e3,
  '6h': 21600e3,
  '24h': 864e5,
  '3d': 2592e5,
  '7d': 6048e5,
  '30d': 2592e6,
  '90d': 7776e6,
};

/**
 * @param {string} slug
 * @returns {Promise<Object>} the dashboard record; `id` is the slug
 */
export const getDashboard = (slug) =>
  request(`/api/dashboards/${encodeURIComponent(slug)}`);

/**
 * The owner's dashboards. No cursor: `limit=50` is the hard per-user cap
 * (`MAX_DASHBOARDS_PER_USER`), so one page provably covers every account. The
 * API's own default page is only 25, which would silently hide the oldest
 * dashboards — a parameter here would otherwise be a pagination claim the
 * surface does not honour.
 *
 * @returns {Promise<{items: Object[], nextCursor: string|null}>}
 */
export const listDashboards = () => request('/api/dashboards?limit=50');

/**
 * Seed the caller's first dashboard from their own readings.
 *
 * Rejects 409 `already_seeded` when they have one or have had one, and 422
 * `no_data` when no device of theirs has ever reported.
 */
export const seedExample = () =>
  request('/api/dashboards/example', { method: 'POST' });

/** @returns {Promise<null>} the endpoint answers 204. */
export const deleteDashboard = (slug) =>
  request(`/api/dashboards/${encodeURIComponent(slug)}`, { method: 'DELETE' });

/**
 * Mint the single-use render URL for the iframe. The API assembles origin,
 * path and parameter, so this client never knows them. The nonce is spent by
 * the first GET, so re-mint whenever the frame will reload.
 */
export const mintRenderUrl = (slug) =>
  request(`/api/dashboards/${encodeURIComponent(slug)}/render-token`, { method: 'POST' });

/** Drops empty values so absent optional parameters never reach the API. */
const qs = (params) => {
  const u = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  });
  return u.toString();
};

/**
 * Turns a stored manifest declaration into exactly one API call.
 *
 * Every parameter comes from the manifest or from host state. The frame
 * supplies only the query's NAME; nothing it sends reaches this function.
 *
 * @param {Object} decl      a query from the dashboard's stored manifest
 * @param {string} timeRange the host's current range
 * @param {AbortSignal} signal
 * @returns {Promise<any>}
 */
export const runQuery = (decl, timeRange, signal) => {
  const device = encodeURIComponent(decl.device);
  const op = decl.op === undefined ? 'series' : decl.op;

  if (op === 'latest') {
    return request(`/api/devices/${device}/readings`, { signal });
  }

  const span = RANGE_MS[decl.range ?? timeRange] ?? RANGE_MS['7d'];
  const to = Date.now();
  const from = to - span;

  if (op === 'coverage') {
    return request(`/api/devices/${device}/coverage?${qs({ from, to })}`, { signal });
  }

  return request(
    `/api/devices/${device}/aggregate?${qs({
      channel: decl.channel,
      from,
      to,
      bucket: decl.bucket ?? 'auto',
      agg: decl.agg ?? 'avg',
      validRange: decl.validRange
        ? `${decl.validRange.min ?? ''},${decl.validRange.max ?? ''}`
        : undefined,
    })}`,
    { signal }
  );
};
